import React, { useEffect, useState } from "react";
import { fetchRates, fetchGold, formatNum } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { TrendingUp, TrendingDown, Loader2, RefreshCcw, Coins, DollarSign } from "lucide-react";

function StatRow({ left, code, name, unit, buy, sell, change, decimals = 4, accent }) {
  const up = change != null && change >= 0;
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto_auto] md:grid-cols-[auto_2fr_1fr_1fr_1fr] items-center gap-4 px-4 md:px-6 py-4 border-b border-slate-100 hover:bg-slate-50/70 transition-colors group">
      <div className="text-2xl md:text-3xl">{left}</div>
      <div>
        <div className="font-display font-semibold text-slate-950 text-sm md:text-base">
          {code}
          {unit && unit !== 1 && (
            <span className="ml-1.5 text-[10px] font-mono-fin text-slate-400">×{unit}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate">{name}</div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5 hidden md:block">Alış</div>
        <div className="font-mono-fin font-semibold text-slate-950 text-sm md:text-base">
          {formatNum(buy, decimals)}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5 hidden md:block">Satış</div>
        <div className="font-mono-fin font-semibold text-sm md:text-base" style={{ color: accent || "#0F172A" }}>
          {formatNum(sell, decimals)}
        </div>
      </div>
      <div className="text-right">
        {change != null ? (
          <div
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono-fin font-semibold ${
              up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`}
          >
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {up ? "+" : ""}
            {formatNum(change, 2)}%
          </div>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </div>
    </div>
  );
}

export default function RatesBoard() {
  const [rates, setRates] = useState(null);
  const [gold, setGold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadAll = async (showSpinner = true) => {
    if (showSpinner) setRefreshing(true);
    try {
      const [r, g] = await Promise.all([fetchRates(), fetchGold()]);
      setRates(r);
      setGold(g);
      setUpdatedAt(new Date());
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAll(false);
    const id = setInterval(() => loadAll(false), 90000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative" id="rates" data-testid="rates-board">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-bold mb-2">
              Canlı Piyasa Verileri
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-950 leading-tight tracking-tighter">
              Anlık Kurlar Tablosu
            </h2>
            <p className="text-slate-500 mt-2 text-sm md:text-base max-w-2xl">
              Anlık piyasa verileriyle güncellenen döviz ve altın fiyatları. Her 90 saniyede bir otomatik yenilenir.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500 font-mono-fin hidden md:block">
              {updatedAt ? `Son güncelleme: ${updatedAt.toLocaleTimeString("tr-TR")}` : "—"}
            </div>
            <button
              onClick={() => loadAll(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-950 text-white text-xs font-semibold hover:bg-brand-gold transition-colors"
              disabled={refreshing}
              data-testid="rates-refresh-button"
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Yenile
            </button>
          </div>
        </div>

        <Tabs defaultValue="doviz" className="w-full">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-full mb-6 w-full md:w-auto inline-flex">
            <TabsTrigger
              value="doviz"
              className="rounded-full data-[state=active]:bg-slate-950 data-[state=active]:text-white px-5 py-2.5 text-sm font-semibold"
              data-testid="rates-tab-doviz"
            >
              <DollarSign className="w-4 h-4 mr-1.5" />
              Döviz
            </TabsTrigger>
            <TabsTrigger
              value="altin"
              className="rounded-full data-[state=active]:bg-brand-gold data-[state=active]:text-white px-5 py-2.5 text-sm font-semibold"
              data-testid="rates-tab-gold"
            >
              <Coins className="w-4 h-4 mr-1.5" />
              Altın
            </TabsTrigger>
          </TabsList>

          {/* Döviz */}
          <TabsContent value="doviz" className="mt-0">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="hidden md:grid grid-cols-[auto_2fr_1fr_1fr_1fr] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                <span className="w-8" />
                <span>Para Birimi</span>
                <span className="text-right">Alış</span>
                <span className="text-right">Satış</span>
                <span className="text-right">Değişim</span>
              </div>
              {loading || !rates ? (
                <div className="py-20 flex items-center justify-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                rates.rates.map((r) => (
                  <StatRow
                    key={r.code}
                    left={<span>{r.flag}</span>}
                    code={r.code}
                    name={r.name}
                    unit={r.unit}
                    buy={r.forex_buying}
                    sell={r.forex_selling}
                    change={r.change}
                    accent="#0F172A"
                  />
                ))
              )}
              <div className="px-6 py-4 bg-slate-50/50 text-xs text-slate-500 border-t border-slate-100">
                Güncelleme:{" "}
                <span className="font-mono-fin">{rates?.date || "—"}</span>
              </div>
            </div>
          </TabsContent>

          {/* Altın */}
          <TabsContent value="altin" className="mt-0">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="hidden md:grid grid-cols-[auto_2fr_1fr_1fr_1fr] gap-4 px-6 py-3 bg-amber-50/50 border-b border-amber-100 text-[10px] uppercase tracking-[0.18em] text-amber-800 font-bold">
                <span className="w-8" />
                <span>Altın</span>
                <span className="text-right">Alış (₺)</span>
                <span className="text-right">Satış (₺)</span>
                <span className="text-right">Marj</span>
              </div>
              {loading || !gold ? (
                <div className="py-20 flex items-center justify-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                gold.items.map((g) => (
                  <StatRow
                    key={g.code}
                    left={<span>🥇</span>}
                    code={g.code}
                    name={g.name}
                    unit={1}
                    buy={g.buy}
                    sell={g.sell}
                    change={g.change}
                    decimals={2}
                    accent="#A8884A"
                  />
                ))
              )}
              <div className="px-6 py-4 bg-amber-50/40 text-xs text-amber-900 border-t border-amber-100">
                <span className="font-semibold">Bilgi:</span> Altın fiyatları anlık piyasa verileriyle güncellenir. İşlem anında kur değişebilir.
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
